import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";

import socket from 'services/socket';
import { dateFromNow, getAvatar } from 'utils/helpers';
import { useStore } from '../../store';

// import IconChatUser from "./img/user.svg";
import IconChats from './img/chats.png';
import UsersMessage from './UsersMessage';
import SystemMessage from './SystemMessage';

const Chats = () => {
	const [chats, setChats] = useState([]);
	const [currentChat, setCurrentChat] = useState(null);
	const [messages, setMessages] = useState([]);
	const [canMessage, setCanMessage] = useState();
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const messagesContainer = useRef();

	const store = useStore();

	useEffect(() => {
		if (store.auth.isSocketAuth) {
			socket.emit('reqChats');
		}
	}, [store.auth.isSocketAuth]);

	useEffect(() => {
		const onResChats = (data) => {
			setChats(data);
		};

		const onResDialog = ({ messages, canMessage }) => {
			setCanMessage(canMessage)
			setMessages(messages);
		};

		const onNewMessage = ({ chat, message }) => {
			if (currentChat === chat) {
				setMessages((prev) => [...prev, message]);
				if (!message.isMe) {
					socket.emit('makeReaded', chat);
				}
			}

			setChats((prev) =>
				prev.map((item) => {
					if (item._id === chat) {
						if (currentChat !== chat && !message.isMe) {
							item.unreaded += 1;
						}
						item.message = message;
					}
					return { ...item };
				})
			);
		};

		const onChatReaded = ({ chat, from }) => {
			if (chat === currentChat) {
				setMessages((prev) =>
					prev.map((item) => {
						if (item.from === from) item.isReaded = true;
						return { ...item };
					})
				);
			}
		};

		const onNewChat = () => {
			socket.emit('reqChats');
		};

		socket.on('newChat', onNewChat);
		socket.on('resChats', onResChats);
		socket.on('resDialog', onResDialog);
		socket.on('newMessage', onNewMessage);
		socket.on('chatReaded', onChatReaded);

		return () => {
			socket.off('newChat', onNewChat);
			socket.off('resChats', onResChats);
			socket.off('resDialog', onResDialog);
			socket.off('newMessage', onNewMessage);
			socket.off('chatReaded', onChatReaded);
		};
	}, [currentChat]);

	const scrollMessages = useCallback(() => {
		if (messagesContainer.current) {
			messagesContainer.current.scrollTop =
				messagesContainer.current.scrollHeight;
		}
	}, []);

	useEffect(() => {
		scrollMessages();
	}, [messages, scrollMessages]);

	useEffect(() => {
		const interval = setInterval(() => {
			setMessages((prev) => [...prev]);
			setChats((prev) => [...prev]);
		}, 60000);

		return () => clearInterval(interval);
	}, []);

	useEffect(() => {
		const hasUnreaded = chats.some((chat) => chat.unreaded > 0);
		store.notif.setUnreadedChats(hasUnreaded);
	}, [store, chats]);

	const toggleChat = useCallback((id) => {
		setCurrentChat((prev) => (prev === id ? null : id));
		setMessages([]);

		if (id) {
			socket.emit('reqDialog', id);

			setChats((prev) =>
				prev.map((chat) => {
					if (chat._id === id) chat.unreaded = 0;
					return { ...chat };
				})
			);
		}

		setIsMobileMenuOpen(false);
	}, []);

	const sendMessage = useCallback(
		(event) => {
			event.preventDefault();
			const content = event.target.content.value;
			event.target.content.value = '';

			socket.emit('sendMessage', { chat: currentChat, content });
		},
		[currentChat]
	);

	return (
		<div className="page chats">
			{/* Mobile menu button */}
			<button
				className="mobile-menu-button md:hidden absolute top-4 left-4 z-10 p-2 rounded-md bg-white text-slate-600 hover:text-slate-900 focus:outline-none shadow-md border border-slate-200"
				onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
			>
				{isMobileMenuOpen ? (
					<XMarkIcon className="h-5 w-5" aria-hidden="true" />
				) : (
					<Bars3Icon className="h-5 w-5" aria-hidden="true" />
				)}
			</button>

			{/* Mobile menu overlay */}
			<div
				className={`fixed inset-0 z-40 transition-opacity duration-300 md:hidden ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
					}`}
			>
				<div
					className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm"
					aria-hidden="true"
					onClick={() => setIsMobileMenuOpen(false)}
				/>
				<div
					className={`fixed inset-y-0 left-0 w-[280px] bg-white shadow-xl transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
						}`}
				>
					<div className="h-full flex flex-col">
						<div className="px-4 py-4 border-b border-slate-200 flex items-center justify-between">
							<h2 className="text-lg font-semibold text-slate-900">All chats</h2>
							<button
								className="p-1 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100"
								onClick={() => setIsMobileMenuOpen(false)}
							>
								<XMarkIcon className="h-5 w-5" aria-hidden="true" />
							</button>
						</div>
						<div className="flex-1 overflow-y-auto">
							{chats.map((chat) => (
								<div
									className={classNames(
										'px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors duration-150',
										{
											'bg-slate-100': currentChat === chat._id,
										}
									)}
									onClick={() => toggleChat(chat._id)}
									key={chat._id}
								>
									<div className="flex items-center space-x-3">
										<img src={getAvatar()} alt="Avatar" className="w-10 h-10 rounded-full" />
										<div className="flex-1 min-w-0">
											<div className="flex justify-between items-center">
												<p className="text-sm font-medium text-slate-900 truncate">
													{chat.companion.name} {chat.companion.surname}
												</p>
												<span className="text-xs text-slate-500">
													{dateFromNow(chat.message?.date_create)}
												</span>
											</div>
											<div className="flex justify-between items-center mt-1">
												<p className="text-sm text-slate-500 truncate">
													{chat.message?.content}
												</p>
												{chat.unreaded > 0 && (
													<span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-blue-500 rounded-full">
														{chat.unreaded}
													</span>
												)}
											</div>
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>

			{/* Desktop dialogs */}
			<div className="dialogs hidden md:block">
				<h2>All chats</h2>
				{chats.map((chat) => (
					<div
						className={classNames('item', {
							current: currentChat === chat._id,
						})}
						onClick={() => toggleChat(chat._id)}
						key={chat._id}
					>
						<img src={getAvatar()} alt="Avatar" className="avatar" />
						<div className="info">
							<div className="custom-flex">
								<div className="name">
									{chat.companion.name} {chat.companion.surname}
								</div>
								<div className="date">
									{dateFromNow(chat.message?.date_create)}
								</div>
							</div>
							<div className="custom-flex bottom">
								<div className="message">{chat.message?.content}</div>
								{chat.unreaded > 0 && (
									<div className="budge">{chat.unreaded}</div>
								)}
							</div>
						</div>
					</div>
				))}
			</div>

			{currentChat === null && (
				<div className="chat center">
					<div className="empty">
						<img src={IconChats} alt="Choose a chat" />
						<div className="title">Choose a chat</div>
					</div>
				</div>
			)}

			{!!currentChat && (
				<div className="chat single">
					<div className="messages base-scroll" ref={messagesContainer}>
						{messages.map((item) => (
							item.from === "system" ?
								<SystemMessage item={item} key={item._id} />
								:
								<UsersMessage item={item} key={item._id} />
						))}
					</div>

					<form className="bottom" onSubmit={sendMessage}>
						<input name="content" type="text" disabled={!canMessage} placeholder={canMessage ? "Your message" : "You can not message this user unless you have a mutual job"} />
						<button disabled={!canMessage} className={classNames({ disabled: !canMessage })}>Send</button>
					</form>
				</div>
			)}
		</div>
	);
};

export default observer(Chats);
